import {
  Box,
  ButtonProps,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import VerifiedIcon from "@mui/icons-material/Verified";
import { GridDeleteIcon } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { COLORS } from "../../assets/theme";
import Actions from "../../components/common/Actions";
import ButtonsRow from "../../components/common/ButtonsRow";
import { ICustomButtonProps } from "../../components/common/CustomButton";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import CustomMonthYearPicker from "../../components/common/CustomMonthYearPicker";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import ToastNotification from "../../components/ToastNotification";
import {
  fetchCurrency,
} from "../../services/CurrencyService";
import {
  fetchProfile,
  useProfilesDropdown,
  useProfilesQuery,
} from "../../services/ProfilesService";
import {
  BulkGenerateRequest,
  confirmServiceProviderApi,
  createServiceProviderBill,
  deleteServiceProviderBills,
  exportServiceProviderBill,
  fetchServiceProvidersByProfile,
  fetchServiceProviderBillHistory,
  pdfServiceProviderApi,
  pdfServiceProviderDetailsApi,
  ServiceProviderBillType,
  useServiceProviderBillById,
  useServiceProviderBillQuery,
} from "../../services/ServiceProviderBillService";
import { DateRange, IField, Params } from "../../types/table/interfaces.type";
import { pageAsOptions } from "../../utils/commonDataUtils";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { ReactComponent as Confirm } from "../../assets/icons/confirm.svg";
import ConfirmAmountsDialog from "../../components/ConfirmAmountsDialog";

import History from "../../components/History";
import GoldMultiSelectField, {
  MONTH_OPTIONS,
} from "../../components/GoldMultiSelectField";
import { hasJwtPermission } from "../../services/ServicesHelpers";

interface Option {
  label: string;
  value: string;
}
const ServiceProviderBillPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [filters, setFilters] = useState<Params<ServiceProviderBillType>>({});
  const [appliedFilters, setAppliedFilters] = useState<
    Params<ServiceProviderBillType & DateRange>
  >({
    search: "",
    profileId: "",
  });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [trafficDate, setTrafficDate] = useState<Date | null>(null);
  const [profileOptions, setProfileOptions] = useState<Option[]>([]);
  const [refOptions, setRefOptions] = useState<Option[]>([]);

  const [profileMap, setProfileMap] = useState<
    Record<
      string,
      { name: string; accountManagerEmail: string; currencyId: string }
    >
  >({});
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [filterDeleted, setFilterDeleted] = useState(false);

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [filters]);

  const { data: dataProfile } = useProfilesQuery();
  const { data: dataProfiledropdown = [] } = useProfilesDropdown(filterDeleted);

  useEffect(() => {
    if (dataProfile) {
      setProfileOptions(dataProfile.profileOptions);
      setProfileMap(dataProfile.profileMap);
    }
  }, [queryClient, dataProfile]);

  const dialogRef = useRef<ServiceProviderBillDialogRef>(null);

  const handleOpenDialog = (mode: "add" | "history", id?: string | number) => {
    dialogRef.current?.openDialog(mode, id);
  };
  const handleOpenConfirmDialog = () => {
    dialogRef.current?.openConfirmDialog();
  };

  const { data: serviceProviderBill } = useServiceProviderBillQuery({});

  useEffect(() => {
    if (serviceProviderBill) {
      try {
        const ServiceProviderBillOptions = pageAsOptions({
          ...serviceProviderBill,
          content: serviceProviderBill.content.map((bill: any) => ({
            id: bill.id,
            name: bill.ref,
            value: bill.ref,
          })),
        }) as Option[];
        setRefOptions(ServiceProviderBillOptions);
      } catch (error: any) {
        console.error("Error processing service provider bill options:", error);
      }
    }
  }, [serviceProviderBill]);

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: fromDate,
      toDate: toDate,
      startDate: trafficDate ?? undefined,
      profileId: filters.profileId,
      excludeZero: filters.excludeZero,
      status: filters.status,
    }));
    setSelectedRowIds([]);
  };

  const onRowSelect = (row: ServiceProviderBillType, checked: boolean) => {
    setSelectedRowIds((prevSelected) => {
      const newSelected = checked
        ? [...prevSelected, String(row.id)]
        : prevSelected.filter((id) => id !== String(row.id));
      return newSelected;
    });
  };

  const isRowSelected = (row: ServiceProviderBillType) =>
    selectedRowIds.includes(String(row.id));

  const handleOpenDeleteDialog = () => {
    if (selectedRowIds.length === 0) {
      ToastNotification.warning(
        "Please select at least one invoice to delete."
      );
      return;
    }
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteInvoices = async () => {
    setIsDeleting(true);
    try {
      await deleteServiceProviderBills(selectedRowIds);
      ToastNotification.success("Service provider invoices deleted successfully!");
      handleCloseDeleteDialog();
      setSelectedRowIds([]);
      queryClient.invalidateQueries();
    } catch (error: any) {
      ToastNotification.error(error.message || "Failed to delete invoices.");
    } finally {
      setIsDeleting(false);
    }
  };

  const isConfirmActionDisabled = (row: any) =>
    row?.status === "CONFIRMED" ||
    !hasJwtPermission("CONFIRM_SERVICE_PROVIDER_INVOICE");

  const actions = Actions({
    onViewHistory: {
      handler: (id: string | number) => {
        setSelectedId(String(id));
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_SERVICE_PROVIDER_INVOICE_HISTORY"),
    },
    onConfirm: {
      handler: (id: string | number) => {
        try {
          handleOpenConfirmDialog();
          setSelectedId(String(id));
        } catch (error: any) {
          console.error(`Error confirming action for ID ${id}:`, error);
        }
      },
      isDisabled: isConfirmActionDisabled,
      icon: (row: any) => {
        const isDisabled = isConfirmActionDisabled(row);
        return (
          <Tooltip title="Confirm">
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: isDisabled ? "transparent" : `${COLORS.successGreen}18`,
                border: isDisabled
                  ? "none"
                  : `1.5px solid ${COLORS.successGreen}55`,
              }}
            >
              <Confirm
                stroke={isDisabled ? "#D3D3D3" : COLORS.successGreen}
                width="18"
                height="22"
              />
            </Box>
          </Tooltip>
        );
      },
    },

    onPdf: {
      handler: (id: string | number) => {
        pdfServiceProviderApi(String(id)).catch(console.error);
      },
      isDisabled: !hasJwtPermission("GET_SERVICE_PROVIDER_INVOICE_PDF"),
    },

    onDetails: {
      handler: (id: string | number) => {
        pdfServiceProviderDetailsApi(String(id)).catch(console.error);
      },
      isDisabled: !hasJwtPermission("GET_SERVICE_PROVIDER_INVOICE_DETAILS_PDF"),
    },
  });
  const autoCompletes = [
    {
      label: "Account Profile",
      value: filters.profileId || "",
      options: dataProfiledropdown,
      isDeleted: filterDeleted,
      clearable: true,
      onToggleDeleted: () => setFilterDeleted((prev) => !prev),
      placeholder: "Select Profile",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProfileOption = dataProfiledropdown.find(
          (option) => option.value === value
        );
        setFilters((prev) => ({
          ...prev,
          profileId: String(selectedProfileOption?.value || ""),
        }));
      },
    },
  ];

  const dropdowns = [
    {
      label: "Exclude Zero",
      value:
        filters.excludeZero === undefined ? "" : String(filters.excludeZero),
      options: [
        { label: "Exclude Zero", value: "true" },
        { label: "Don't Exclude Zero", value: "false" },
      ],
      onChange: (value: string | number | (string | number)[]) => {
        if (Array.isArray(value)) return;
        setFilters((prev) => ({
          ...prev,
          excludeZero:
            value === "true" ? true : value === "false" ? false : undefined,
        }));
      },
    },
    {
      label: "Status",
      value: filters.status || "",
      options: [
        { label: "Generated", value: "GENERATED" },
        { label: "Confirmed", value: "CONFIRMED" },
        { label: "Pending", value: "PENDING" },
        { label: "Error", value: "ERROR" },
      ],
      placeholder: "unselect",
      onChange: (value: string | number | (string | number)[]) => {
        if (Array.isArray(value)) return;
        setFilters((prev) => ({
          ...prev,
          status: value ? (String(value) as any) : undefined,
        }));
      },
    },
  ];

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_SERVICE_PROVIDER_INVOICES",
    },
  ];

  const form2Buttons: ICustomButtonProps[] = [
    {
      label: "Generate invoice",
      size: isSmallScreen ? "medium" : "large",
      variant: "outlined",
      textColor: COLORS.GOLD,
      borderColor: COLORS.GOLD,
      borderRadius: "8px",
      bold: "bold",
      onClick: () => handleOpenDialog("add"),
      permission: "BULK_GENERATE_SERVICE_PROVIDER_INVOICE",
    },
  ];

  const fields = useMemo<IField<ServiceProviderBillType>[]>(() => [
    {
      key: "profileId",
      header: "Account Profile",
      transform: async (t) => {
        if (Array.isArray(t.profileId)) {
          const profileNames = t.profileId
            .map((id) => profileMap[id]?.name)
            .join(", ");
          return profileNames;
        }
        const profile = profileMap[String(t.profileId)];
        return profile?.name;
      },
    },
    {
      key: "status",
      header: "Status",
      transform: (row) => {
        const status = String(row.status)?.toUpperCase();
        switch (status) {
          case "GENERATED":
            return (
              <Tooltip title="Generated">
                <CheckCircleIcon sx={{ color: "green" }} />
              </Tooltip>
            );
          case "CONFIRMED":
            return (
              <Tooltip title="Confirmed">
                <VerifiedIcon sx={{ color: "#1976d2" }} />
              </Tooltip>
            );
          case "PENDING":
            return (
              <Tooltip title="Pending">
                <HourglassEmptyIcon sx={{ color: "orange" }} />
              </Tooltip>
            );
          case "ERROR":
            return (
              <Tooltip title="Error">
                <CancelIcon sx={{ color: "red" }} />
              </Tooltip>
            );
          default:
            return status || "-";
        }
      },
    },
    {
      key: "createdDate",
      header: "Created Date",
      transform: (t) => t.createdDate ? new Date(t.createdDate).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "UTC" }) : "-",
    },
    {
      key: "startDate",
      header: "Invoice Month",
      transform: (t) => {
        if (!t.startDate) return "-";

        const formatFullDate = (date: Date | string) =>
          new Date(date).toLocaleString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            timeZone: "UTC",
          });

        const monthYear = new Date(t.startDate).toLocaleString("en-GB", {
          month: "short",
          year: "numeric",
          timeZone: "UTC",
        });

        return (
          <Tooltip
            title={
              <Box>
                <Typography variant="body2">
                  Start Date: {formatFullDate(t.startDate)}
                </Typography>
                <Typography variant="body2">
                  End Date: {t.endDate ? formatFullDate(t.endDate) : "-"}
                </Typography>
              </Box>
            }
          >
            <span>{monthYear}</span>
          </Tooltip>
        );
      },
    },
    {
      key: "amount",
      header: "System Amount",
      transform: (t) =>
        t.amount !== undefined && t.amount !== null
          ? Number(t.amount).toFixed(3)
          : "-",
    },
    { key: "confirmedAmount", header: "Confirmed Amount" },
    {
      key: "id",
      header: "Currency",
      transform: async (row) => {
        const profileId = Array.isArray(row.profileId)
          ? row.profileId[0]
          : row.profileId;
        if (!profileId) return "-";
        try {
          const profile = await queryClient.fetchQuery({
            queryKey: ["profile", profileId],
            queryFn: () => fetchProfile(String(profileId)),
          });
          if (!profile?.currencyId) return "-";
          const currency = await queryClient.fetchQuery({
            queryKey: ["currency", profile.currencyId],
            queryFn: () => fetchCurrency(Number(profile.currencyId)),
          });
          return currency?.name ?? "-";
        } catch {
          return "-";
        }
      },
    },
  ], [queryClient, profileMap]);

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="Service Provider Invoice"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginBottom={1}
      />
      <Box sx={{ mb: 3, mt: 2 }}>
        <SearchForm
          dateComponent={
            <Box display="flex" flexWrap="wrap" gap={2}>
              <CustomDateRangePicker
                label="Creation Date Range"
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={setFromDate}
                setToDate={setToDate}
              />
              <CustomMonthYearPicker
                label="Invoice Month"
                value={trafficDate}
                onChange={setTrafficDate}
              />
            </Box>
          }
          autoCompletes={autoCompletes}
          dropdowns={dropdowns}
          buttons={buttons}
        />
      </Box>
      <Box sx={{ mb: 3 }}>
        <ButtonsRow buttons={form2Buttons} />
      </Box>
      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Service Provider Invoices List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search || ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              variant: "outlined" as ButtonProps["variant"],
              startIcon: <GridDeleteIcon />,
              onClick: handleOpenDeleteDialog,
              textColor: COLORS.iconColor,
              permission: "DELETE_SERVICE_PROVIDER_INVOICE",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: () => {
                (async () => {
                  try {
                    await exportServiceProviderBill(appliedFilters);
                    ToastNotification.success("Export completed successfully!");
                  } catch (error: any) {
                    ToastNotification.error(
                      error.message || "Export failed. Please try again."
                    );
                  }
                })().catch(console.error);
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_SERVICE_PROVIDER_INVOICES_CSV",
            },
          ]}
        />
        <CustomTable<ServiceProviderBillType>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useServiceProviderBillQuery}
          actions={actions}
          checkboxSelect={true}
          onRowSelect={onRowSelect}
          isRowSelected={isRowSelected}
          sortableFields={["createdDate", "status", "startDate", "endDate", "amount"]}
        />
      </Box>
      <ServiceProviderBillDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        appliedFilters={appliedFilters}
        selectedId={selectedId}
        Profiles={profileOptions}
        refOptions={refOptions}
        profileMap={profileMap}
      />

      {/* Delete Dialog */}
      <CustomDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        mode="delete"
        title="Delete Invoices"
        fields={[
          {
            type: "custom",
            component: (
              <Box>
                <Typography>
                  Are you sure you want to delete {selectedRowIds.length}{" "}
                  selected invoice(s)?
                </Typography>
              </Box>
            ),
          },
        ]}
        buttons={[
          {
            label: isDeleting ? "Deleting..." : "Delete",
            variant: "contained",
            bgColor: COLORS.GOLD,
            textColor: COLORS.iconColor,
            borderRadius: "12px",
            onClick: handleDeleteInvoices,
            disabled: isDeleting || !hasJwtPermission("DELETE_SERVICE_PROVIDER_INVOICE"),
          },
        ]}
      />
    </Box>
  );
};

export interface ServiceProviderBillDialogRef {
  openDialog: (mode: "add" | "history", id?: string | number) => Promise<void>;
  openConfirmDialog: () => void;
}

interface ServiceProviderBillDialogProps {
  onRefresh: () => void;
  Profiles: Option[];
  refOptions: Option[];
  appliedFilters: Params<ServiceProviderBillType & DateRange>;
  selectedId: string | undefined;
  profileMap: Record<string, { name: string }>;
}

interface DialogFormData {
  months?: number[];
  year?: number;
  serviceProviderId?: number[];
  profileId?: string | string[];
  confirmedAmount?: number;
  confirmedRef?: string;
  amount?: number;
  ref?: string;
  createdBy?: string;
  confirmedBy?: string;
}

const ServiceProviderBillDialog = forwardRef<
  ServiceProviderBillDialogRef,
  ServiceProviderBillDialogProps
>(
  (
    { onRefresh, Profiles, selectedId, refOptions, appliedFilters, profileMap },
    ref
  ) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<"add" | "history">("add");
    const [dialogData, setDialogData] = useState<DialogFormData>({});

    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [accountsByProfile, setAccountsByProfile] = useState<Option[]>([]);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const { data: selectedBillData } = useServiceProviderBillById(
      selectedId ? Number(selectedId) : 0
    );

    const profileSelectOptions = useMemo(
      () =>
        Profiles.map((profile) => ({
          label: profile.label,
          value: String(profile.value),
        })),
      [Profiles]
    );

    const selectedProfileOptions = useMemo(() => {
      const selectedProfileIds = Array.isArray(dialogData.profileId)
        ? dialogData.profileId.map(String)
        : dialogData.profileId
          ? [String(dialogData.profileId)]
          : [];

      return profileSelectOptions.filter((option) =>
        selectedProfileIds.includes(String(option.value))
      );
    }, [profileSelectOptions, dialogData.profileId]);

    const selectedServiceProviderOptions = useMemo(
      () =>
        accountsByProfile.filter((option) =>
          (dialogData.serviceProviderId || [])
            .map(String)
            .includes(String(option.value))
        ),
      [accountsByProfile, dialogData.serviceProviderId]
    );

    const selectedMonthOptions = useMemo(
      () =>
        MONTH_OPTIONS.filter((option) =>
          (dialogData.months || []).map(String).includes(String(option.value))
        ),
      [dialogData.months]
    );

    useImperativeHandle(ref, () => ({
      openDialog: async (mode: "add" | "history", id?: string | number) => {
        setDialogMode(mode);

        if (mode === "add") {
          setDialogData({
            months: [new Date().getMonth() + 1],
            year: new Date().getFullYear(),
            serviceProviderId: [],
            profileId: [],
          });
        }

        setErrors({});
        setDialogOpen(true);
        setIsSaveDisabled(true);
      },
      openConfirmDialog: () => {
        setConfirmDialogOpen(true);
      },
    }));

    useEffect(() => {
      if (confirmDialogOpen && selectedBillData) {
        setDialogData(selectedBillData);
        console.log("Selected Service Provider Bill Data:", selectedBillData);
      }
    }, [confirmDialogOpen, selectedBillData]);

    const validateField = (field: string, value: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };

    useEffect(() => {
      console.log("Dialog Data:", dialogData);

      const isAnyFieldEmpty =
        !(Array.isArray(dialogData.months) && dialogData.months.length > 0) ||
        !dialogData.year ||
        !dialogData.profileId ||
        !dialogData.serviceProviderId;

      setIsSaveDisabled(isAnyFieldEmpty);
    }, [dialogData]);

    const closeDialog = () => {
      setDialogOpen(false);
      setDialogData({});
      setErrors({});
      setIsSaveDisabled(true);
    };
    const closeConfirmDialog = () => {
      setConfirmDialogOpen(false);
      setDialogData((prev) => ({
        ...prev,
        confirmedAmount: undefined,
        ref: "",
      }));
      setErrors({});
      setIsUploading(false);
    };

    const handleSave = async () => {
      try {
        const serviceProviderIds = Array.isArray(
          dialogData.serviceProviderId
        )
          ? dialogData.serviceProviderId!.filter(
              (id: number): id is number => typeof id === "number"
            )
          : dialogData.serviceProviderId !== undefined
          ? [dialogData.serviceProviderId]
          : [];

        const periods = (dialogData.months || []).map((month) => ({
          month,
          year: dialogData.year ?? new Date().getFullYear(),
        }));

        const requestData: BulkGenerateRequest = {
          connectionProfileIds: serviceProviderIds,
          periods,
        };

        console.log("Request Data:", requestData);

        const response = await createServiceProviderBill(requestData);

        if (response.failureCount > 0) {
          const failedPeriods = response.results
            .filter((r) => !r.success)
            .map(
              (r) =>
                `${r.month}/${r.year}${
                  r.errorMessage ? `: ${r.errorMessage}` : ""
                }`
            )
            .join(", ");
          ToastNotification.error(failedPeriods);
        }

        onRefresh();
        closeDialog();
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    useEffect(() => {
      const isAnyFieldEmpty =
        !Array.isArray(dialogData.months) ||
        dialogData.months.length === 0 ||
        !dialogData.year ||
        !Array.isArray(dialogData.profileId) ||
        dialogData.profileId.length === 0 ||
        !Array.isArray(dialogData.serviceProviderId) ||
        dialogData.serviceProviderId.length === 0;

      setIsSaveDisabled(isAnyFieldEmpty);
    }, [dialogData]);

    const fetchDynamicAccountsForProfiles = async (
      profileIds: string[]
    ): Promise<void> => {
      try {
        const serviceProviderPromises = profileIds.map((profileId) =>
          fetchServiceProvidersByProfile(profileId)
        );
        const serviceProviderResponses = await Promise.all(serviceProviderPromises);

        const combinedServiceProviders = serviceProviderResponses
          .flatMap((response) => response.content || [])
          .map((serviceProvider: { id: number; name: string }) => ({
            label: serviceProvider.name,
            value: String(serviceProvider.id),
          }));

        setAccountsByProfile(combinedServiceProviders);

        setDialogData((prev) => ({
          ...prev,
          serviceProviderId: combinedServiceProviders.map((sp) =>
            Number(sp.value)
          ),
        }));
      } catch (error: any) {
        console.error("Error fetching service providers for selected profiles:", error);
        setAccountsByProfile([]);
        setDialogData((prev) => ({
          ...prev,
          serviceProviderId: [],
        }));
      }
    };

    const handleConfirm = async () => {
      if (!selectedId) {
        ToastNotification.error("No ID selected.");
        return;
      }

      setIsUploading(true);
      try {
        console.log("Dialog Data:", dialogData.confirmedRef);
        await confirmServiceProviderApi(
          selectedId,
          Number(dialogData.confirmedAmount),
          String(dialogData.confirmedRef)
        );

        ToastNotification.success("Service Provider Bill Confirmed");
        closeConfirmDialog();

        onRefresh();
      } catch (error: any) {
        console.error("Error uploading file:", error);
        ToastNotification.error(
          error.message || "Service Provider Bill failed to confirm."
        );
      } finally {
        setIsUploading(false);
      }
    };

    const handleConfirmDialogChange = (
      field: string,
      value: string | number
    ) => {
      setDialogData((prev) => ({
        ...prev,
        [field]: value === "" ? undefined : value,
      }));
    };

    const fieldHistory = useMemo<IField<ServiceProviderBillType>[]>(() => [
      {
        key: "profileId",
        header: "Account Profile",
        transform: (t) => {
          if (
            !t.profileId ||
            (Array.isArray(t.profileId) && t.profileId.length === 0)
          )
            return "Unknown";

          if (Array.isArray(t.profileId)) {
            const profileNames = t.profileId
              .map((id) => profileMap[id]?.name || "Unknown")
              .join(", ");
            return profileNames || "Unknown";
          }
          const profile = profileMap[t.profileId as string];
          return profile?.name || "Unknown";
        },
      },
      { key: "ref", header: "Invoice Name" },
      {
        key: "createdDate",
        header: "Invoice Date",
        transform: (t) => t.createdDate ? new Date(t.createdDate).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "UTC" }) : "-",
      },
      {
        key: "startDate",
        header: "Start Date",
        transform: (t) => t.startDate ? new Date(t.startDate).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "UTC" }) : "-",
      },
      {
        key: "endDate",
        header: "End Date",
        transform: (t) => t.endDate ? new Date(t.endDate).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "UTC" }) : "-",
      },
      {
        key: "amount",
        header: "System Amount",
        transform: (t) =>
          t.amount !== undefined && t.amount !== null
            ? Number(t.amount).toFixed(3)
            : "-",
      },
      {
        key: "confirmedAmount",
        header: "Confirmed Amount",
      },
      {
        key: "createdBy",
        header: "Created By",
      },
      {
        key: "confirmedBy",
        header: "Confirmed By",
      },
    ], [profileMap]);

    return (
      <>
        <CustomDialog
          open={dialogOpen}
          onClose={closeDialog}
          mode={dialogMode}
          title={dialogMode === "history" ? "History" : "Generate New Invoice"}
          fields={
            dialogMode === "history"
              ? [
                  {
                    type: "custom",
                    component: (
                      <History
                        filters={appliedFilters as Params<ServiceProviderBillType>}
                        fields={fieldHistory}
                        fetchFunc={({ size = 10 }) =>
                          fetchServiceProviderBillHistory(Number(selectedId), {
                            ...appliedFilters,
                            size,
                          })
                        }
                      />
                    ),
                  },
                ]
              : [
                  {
                    type: "custom",
                    fullWidth: true,
                    component: (
                      <GoldMultiSelectField
                        label="Month"
                        placeholder="Select Month(s)"
                        options={MONTH_OPTIONS}
                        value={selectedMonthOptions}
                        onChange={(selected) => {
                          const selectedMonths = selected.map((option) =>
                            Number(option.value)
                          );
                          setDialogData((prev) => ({
                            ...prev,
                            months: selectedMonths,
                          }));
                          validateField("months", selectedMonths.join(","));
                        }}
                        error={!!errors.months}
                        helperText={
                          errors.months
                            ? "At least one month must be selected"
                            : ""
                        }
                      />
                    ),
                  },
                  {
                    type: "select",
                    label: "Year",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.year || new Date().getFullYear(),
                    onChange: (value) => {
                      setDialogData((prev) => ({
                        ...prev,
                        year: Number(value),
                      }));
                      validateField("year", value as string);
                    },
                    options: Array.from(
                      { length: new Date().getFullYear() - 2000 + 1 },
                      (_, index) => ({
                        label: (2000 + index).toString(),
                        value: (2000 + index).toString(),
                      })
                    ),
                    error: !!errors.year,
                    helperText: errors.year ? "Year is required" : "",
                  },

                  {
                    type: "custom",
                    fullWidth: true,
                    component: (
                      <GoldMultiSelectField
                        label="Profile"
                        placeholder="Select Profile"
                        options={profileSelectOptions}
                        value={selectedProfileOptions}
                        onChange={async (selected) => {
                          const selectedProfiles = selected.map((option) =>
                            String(option.value)
                          );
                          setDialogData((prev) => ({
                            ...prev,
                            profileId: selectedProfiles,
                          }));
                          validateField(
                            "profileId",
                            selectedProfiles.join(",")
                          );
                          if (selectedProfiles.length > 0) {
                            await fetchDynamicAccountsForProfiles(
                              selectedProfiles
                            );
                          } else {
                            setAccountsByProfile([]);
                          }
                        }}
                        error={!!errors.profileId}
                        helperText={
                          errors.profileId
                            ? "At least one profile must be selected"
                            : ""
                        }
                      />
                    ),
                  },
                  {
                    type: "custom",
                    fullWidth: true,
                    component: (
                      <GoldMultiSelectField
                        label="Service Providers"
                        placeholder="Select Service Providers"
                        options={accountsByProfile}
                        value={selectedServiceProviderOptions}
                        onChange={(selected) => {
                          const stringifiedServiceProviders = selected.map(
                            (option) => String(option.value)
                          );
                          validateField(
                            "serviceProviderId",
                            stringifiedServiceProviders.join(",")
                          );
                          setDialogData((prev) => ({
                            ...prev,
                            serviceProviderId:
                              stringifiedServiceProviders.map(Number),
                          }));
                        }}
                        error={!!errors.serviceProviderId}
                        helperText={
                          errors.serviceProviderId
                            ? "Service Provider is required"
                            : ""
                        }
                      />
                    ),
                  },
                ]
          }
          buttons={
            dialogMode === "history"
              ? []
              : [
                  {
                    label: "Save",
                    variant: "contained",
                    bgColor: COLORS.GOLD,
                    borderRadius: "12px",
                    onClick: handleSave,
                    disabled: isSaveDisabled || !hasJwtPermission("BULK_GENERATE_SERVICE_PROVIDER_INVOICE"),
                  },
                ]
          }
        />
        <ConfirmAmountsDialog
          open={confirmDialogOpen}
          onClose={closeConfirmDialog}
          type="confirm"
          isUploading={isUploading}
          errors={errors}
          dialogData={dialogData}
          onChange={handleConfirmDialogChange}
          onConfirm={handleConfirm}
        />
      </>
    );
  }
);

export default ServiceProviderBillPage;
